
import { defineComponent } from 'vue';
import api from '@/api/admin/hosts';
import utils from '@/utils';
import { IHostDomain, ISite } from '@/types/models';
import agentSites from '@/api/agent/sites';
import agentSetDomain from '@/api/agent/setDomain';
import { HOST_DOMAIN_HONGKONG, HOST_DOMAIN_MAINLAND } from '@/const';
import { IElForm } from '@/types/element';

interface IEditForm {
  siteId: number;
  prefix: string;
  domain: string;
  https: boolean;
  background: boolean;
}

interface IState {
  hostName: string;
  nodeName: string;
  isHongKong: boolean;
  uniqueName: string;
  nickName: string;
  securityKey: string;
  domains: IHostDomain[];
  sites: ISite[];
  agentSitesError: boolean;
  agentSitesAvailable: boolean;
  isEdit: boolean;
  site: ISite;
  editForm: IEditForm;
  loading: boolean;
  guid: string;
  errorMessage: string;
}

export default defineComponent({
  name: 'Domains',
  props: {
    userName: {
      type: String,
      required: true,
    },
    hostId: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      hostName: '',
      nodeName: '',
      isHongKong: false,
      uniqueName: '',
      nickName: '',
      securityKey: '',
      domains: [],
      sites: [],
      agentSitesError: false,
      agentSitesAvailable: true,
      isEdit: false,
      site: {} as ISite,
      editForm: {} as IEditForm,
      loading: false,
      guid: '',
      errorMessage: '',
    } as IState;
  },
  methods: {
    apiGet() {
      api.getDomains(this.userName, this.hostId, (res) => {
        if (!res) return;
        this.hostName = res.hostName;
        this.nodeName = res.nodeName;
        this.isHongKong = res.isHongKong;
        this.uniqueName = res.uniqueName;
        this.nickName = res.nickName;
        this.securityKey = res.securityKey;
        this.domains = res.domains;

        this.agentSitesError = false;
        this.agentSitesAvailable = true;
        agentSites.getSites(
          this.hostName,
          this.nodeName,
          this.isHongKong,
          {
            securityKey: this.securityKey,
          },
          (res2) => {
            this.sites = res2.sites;
          },
          () => {
            this.agentSitesError = true;
            this.agentSitesAvailable = false;
            this.sites = this.getFallbackSites();
          },
        );
      });
    },

    getFallbackSites(): ISite[] {
      const siteIds = new Set<number>([1]);
      for (const domain of this.domains) {
        siteIds.add(domain.siteId);
      }
      return Array.from(siteIds).map((siteId) => {
        const domain = this.domains.find((x) => x.siteId === siteId);
        const prefix = domain?.prefix || '/';
        return {
          guid: '',
          id: siteId,
          createdDate: new Date(),
          lastModifiedDate: new Date(),
          siteDir: '',
          siteName: siteId === 1 ? '主站' : `站点 ${siteId}`,
          siteType: '',
          imageUrl: '',
          keywords: '',
          description: '',
          tableName: '',
          root: siteId === 1 || prefix === '/',
          parentId: 0,
          taxis: 0,
          localUrl: prefix,
          siteUrl: '',
          isSeparatedWeb: false,
          separatedWebUrl: '',
          children: [],
        };
      });
    },

    apiSetDomain() {
      const siteIds = this.sites.map(x => x.id);
      this.loading = true;
      api.setDomain(
        {
          userName: this.userName,
          hostId: this.hostId,
          siteIds,
          siteId: this.editForm.siteId,
          prefix: this.editForm.prefix,
          domain: this.editForm.domain,
          https: this.editForm.https,
          background: this.editForm.background,
        },
        (res) => {
          this.guid = res.value;
          this.apiSetDomainQuery();
        },
      );
    },

    apiSetDomainQuery() {
      setTimeout(() => {
        api.setDomainQuery(this.guid, (res) => {
          if (res.completed) {
            this.loading = false;
            if (res.success) {
              this.isEdit = false;
              if (this.agentSitesAvailable) {
                this.apiAgentSetDomain();
              } else {
                utils.success('自定义域名设置成功');
              }
              this.apiGet();
            } else {
              this.errorMessage = res.errorMessage;
            }
          } else {
            this.apiSetDomainQuery();
          }
        });
      }, 3000);
    },

    apiAgentSetDomain() {
      let siteDomain = this.editForm.https ? 'https://' : 'http://';
      if (this.editForm.domain.indexOf(',') !== -1) {
        siteDomain += this.editForm.domain.substring(0, this.editForm.domain.indexOf(','));
      } else {
        siteDomain += this.editForm.domain;
      }
      const hostDomain = `https://${this.hostName}${this.nodeName}.${this.isHongKong ? HOST_DOMAIN_HONGKONG : HOST_DOMAIN_MAINLAND}`;
      agentSetDomain.setDomain(
        this.hostName,
        this.nodeName,
        this.isHongKong,
        {
          securityKey: this.securityKey,
          hostDomain,
          siteId: this.editForm.siteId,
          siteDomain,
        },
        (res) => {
          if (res.value) {
            utils.success('自定义域名设置成功');
          }
        },
      );
    },

    validateDomain(rule: string, value: string, callback: (error?: Error) => void) {
      const regexp = /^[a-z0-9]+([-.]{1}[a-z0-9]+)*\.[a-z]{2,6}$/i;
      if (value) {
        if (value.indexOf(':') !== -1) {
          callback(new Error('域名不能包含“:”字符'));
          return;
        }
        if (value.indexOf(' ') !== -1) {
          callback(new Error('域名不能包含空白字符'));
          return;
        }
        const domains = value.split(',');
        for (const domain of domains) {
          if (!regexp.test(domain)) {
            callback(new Error('域名格式不正确'));
            return;
          }
        }
      }
      callback();
    },

    getCName() {
      const hostDomain = this.isHongKong ? HOST_DOMAIN_HONGKONG : HOST_DOMAIN_MAINLAND;
      return `${this.nodeName}.${hostDomain}`;
    },

    getSiteDomain(site: ISite) {
      const domain = this.domains.find((x) => x.siteId === site.id);
      if (domain != null) {
        return domain.domain;
      }
      return '';
    },

    getHostUrl(): string {
      return utils.getHostUrl(this.hostName, this.nodeName, this.isHongKong);
    },

    getSitUrl(site: ISite) {
      return this.getHostUrl() + site.localUrl;
    },

    isHttps(url: string) {
      return !!(url && url.startsWith('https://'));
    },

    getDomain(url: string) {
      const r = /:\/\/(.[^/]+)/;
      const arr = (url || '').match(r);
      return arr && arr.length > 1 ? arr[1] : '';
    },

    btnEditClick(site: ISite) {
      this.errorMessage = '';
      this.isEdit = true;
      this.site = site;
      const settings = this.domains.find((x) => x.siteId === site.id);

      if (settings) {
        this.editForm = {
          siteId: site.id,
          prefix: site.localUrl,
          domain: settings.domain,
          https: settings.https,
          background: settings.background,
        };
      } else {
        const domain = site.isSeparatedWeb ? this.getDomain(site.separatedWebUrl) : '';
        const https = site.isSeparatedWeb ? this.isHttps(site.separatedWebUrl) : true;
        this.editForm = {
          siteId: site.id,
          prefix: site.localUrl,
          domain,
          https,
          background: false,
        };
      }
    },

    btnEditSubmitClick() {
      (this.$refs.form as IElForm).validate((valid) => {
        if (valid) {
          this.errorMessage = '';
          this.apiSetDomain();
        }
      });
    },
  },
  watch: {
    hostId: {
      handler(newVal, oldVal) {
        if (newVal !== oldVal) {
          this.apiGet();
        }
      },
      deep: false,
    },
  },
  mounted() {
    this.apiGet();
  },
});
